import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { HasPermissionsService } from '../../shared/services/has-permissions/has-permissions.service';
import {
  COMPANY_ROLE_TO_PERMISSION,
  COMPANY_ACTION,
  CompanyAction,
  normalizeCompanyStatus,
} from '../components/companies/companies.constants';

interface CompanyPermissionsConfig {
  permissions: Record<string, Record<string, string[]>>;
  // status -> role -> form field ids that role may edit. A role absent here edits every field.
  editable_fields?: Record<string, Record<string, string[]>>;
  common_permissions?: Record<string, { hidden?: string[] }>;
}

/**
 * Loads `company-permissions.config.json` and provides config-driven action visibility checks
 * for the company lifecycle (PRD HU5).
 *
 * Mirrors PoPermissionsConfigService, but company has a single lifecycle flow (no source_type
 * dimension), so the config is keyed `permissions[status][role] = actions[]`.
 *
 * `canPerformAction(status, action)` returns `true` if the config allows the action for that
 * status AND the current user has at least one of the permitted roles (checked via
 * HasPermissionsService).
 */
@Injectable({ providedIn: 'root' })
export class CompanyPermissionsConfigService {
  private readonly CONFIG_URL = 'assets/financial-hub/company-permissions.config.json';
  private readonly _config$ = new BehaviorSubject<CompanyPermissionsConfig | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly permissionsService: HasPermissionsService,
  ) {}

  loadConfig(): Observable<CompanyPermissionsConfig> {
    return this.http.get<CompanyPermissionsConfig>(this.CONFIG_URL).pipe(
      tap(config => this._config$.next(config)),
    );
  }

  get config$(): BehaviorSubject<CompanyPermissionsConfig | null> {
    return this._config$;
  }

  canPerformAction(status: string | null, action: CompanyAction): boolean {
    // The config keys are snake_case lowercase, but the company detail endpoint returns the status
    // in UPPER_CASE — normalize here so the lookup matches regardless of the caller's casing.
    const normalized = normalizeCompanyStatus(status);
    if (!normalized) {
      return false;
    }
    const config = this._config$.getValue();
    if (!config) {
      return false;
    }

    const statusConfig = config.permissions?.[normalized];
    if (!statusConfig) {
      return false;
    }

    for (const roleKey of Object.keys(statusConfig)) {
      const allowedActions = statusConfig[roleKey];
      if (!allowedActions.includes(action)) {
        continue;
      }

      const permissionSlug = COMPANY_ROLE_TO_PERMISSION[roleKey];
      if (!permissionSlug) {
        continue;
      }

      if (this.permissionsService.checkPermission(permissionSlug)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Field ids the current user may edit in this status, or `null` when edit is not field-restricted
   * (every edit-capable role the user holds is unrestricted, the user holds none, or the config /
   * status is unknown). Multiple restricted roles are unioned.
   */
  getEditableFieldIds(status: string | null): string[] | null {
    const normalized = normalizeCompanyStatus(status);
    const config = this._config$.getValue();
    const statusConfig = normalized && config ? config.permissions?.[normalized] : null;
    if (!normalized || !config || !statusConfig) {
      return null;
    }

    const restrictions = config.editable_fields?.[normalized];
    const allowed = new Set<string>();

    for (const roleKey of Object.keys(statusConfig)) {
      if (!statusConfig[roleKey].includes(COMPANY_ACTION.EDIT)) {
        continue;
      }
      const permissionSlug = COMPANY_ROLE_TO_PERMISSION[roleKey];
      if (!permissionSlug || !this.permissionsService.checkPermission(permissionSlug)) {
        continue;
      }
      const roleFields = restrictions?.[roleKey];
      if (!roleFields) {
        return null;
      }
      roleFields.forEach(fieldId => allowed.add(fieldId));
    }

    return allowed.size ? Array.from(allowed) : null;
  }

  isCommonActionHidden(action: string, status: string | null): boolean {
    const normalized = normalizeCompanyStatus(status);
    if (!normalized) {
      return false;
    }
    const config = this._config$.getValue();
    if (!config) {
      return false;
    }

    const hiddenStates = config.common_permissions?.[action]?.hidden;
    if (!hiddenStates) {
      return false;
    }

    return hiddenStates.includes(normalized);
  }
}
